import React, {FC, useState, useEffect} from "react"
import styled, {css} from "styled-components"
import {jsPDF} from "jspdf"

import {Body, H2} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import {useGetTermsAndConditions, useAcceptTermsAndConditions} from "@irati/signup/hooks"
import {SignUp} from "@irati/types"
import Markdown from "@irati/ui/src/Markdown"
import ga4Tracking from "@irati-web/tracking"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import {UseQueriesResult} from "@irati/utils/services/apollo"
import GenericModal from "@irati/ui/src/GenericModal"

const StyledModal = styled(GenericModal)`
  ${({theme}) => theme.mediaQueries.maxWidthMedia.phoneLandscape`
    .modal-dialog {
      position: fixed;
      bottom: 0px;
  `}
`

const StyledMarkdown = styled(Markdown)`
  ${({theme}) => css`
    & h1 {
      font-size: 20px;
      margin-bottom: ${theme.spacing.half};
    }
    & p {
      max-width: 100%;
    }
    & ul {
      margin-bottom: ${theme.spacing.half};
    }
  `}
`

const ContainerActions = styled(Flex)`
  ${({theme}) => css`
    ${theme.mediaQueries.maxWidthMedia.phoneLandscape`
      flex-direction: column;
    `}
  `}
`

interface Props {
  show: boolean
  signUp: UseQueriesResult<SignUp>
  termsAccepted: boolean
  onAccept: () => void
  onHide: (accepted?: boolean) => void
}

const TermsAndConditions: FC<Props> = ({show, signUp, onHide, termsAccepted, onAccept}) => {
  const [showModal, setShowModal] = useState(show)
  const [accepted, setAccepted] = useState(!!termsAccepted)
  const [content, setContent] = useState("")
  const [version, setVersion] = useState("")
  const {data} = useGetTermsAndConditions({signupId: signUp?.uuid})
  const [acceptTermsAndConditions] = useAcceptTermsAndConditions({variables: {signupId: signUp?.uuid, version}})

  const {formatMessage} = useIntl()

  const handleDownloadTerms = () => {
    const pdf = new jsPDF("p", "pt", "a4", true)
    const htmlContent = document.querySelector("#contentToPrint")
    pdf.html(htmlContent, {
      callback: (pdfDownload) => {
        pdfDownload.save("termsAndConditions.pdf")
      },
      html2canvas: {
        scale: 0.7
      },
      autoPaging: "text",
      x: 15,
      y: 15
    })
    ga4Tracking.trackEventWithStore("terms_and_conditions_download", "sbs_params", {
      step: "5.2.2",
      email: signUp?.data?.enquiry?.emailAddress,
      page: "payment_page"
    })
  }

  const handleAcceptTerms = () => {
    acceptTermsAndConditions().then(() => {
      ga4Tracking.trackEventWithStore("terms_and_conditions_accepted", "sbs_params", {
        step: "5.2.1",
        email: signUp?.data?.enquiry?.emailAddress,
        page: "payment_page"
      })
      setAccepted(true)
      onAccept()
      setShowModal(false)
      onHide(true)
    })
  }

  const handleCloseModal = () => {
    setShowModal(false)
    onHide(accepted)
  }

  useEffect(() => {
    if (show && data) {
      setContent(data.content)
      setVersion(data.version)
      ga4Tracking.trackEventWithStore("terms_and_conditions_clicked", "sbs_params", {
        step: "5.2",
        email: signUp?.data?.enquiry?.emailAddress,
        page: "payment_page"
      })
    }
    setShowModal(show)
  }, [show])

  return (
    <StyledModal
      proceedButtonText={formatMessage({id: "platform.selfSignUp.terms.update"})}
      hideActions
      show={showModal}
      customWidth={900}
      onHide={handleCloseModal}>
      <H2 marginBottom={2} textAlign="center">
        <FormattedMessage id="platform.selfSignUp.terms.title" />
      </H2>
      <Body maxWidth="100%" marginBottom={3}>
        <StyledMarkdown id="contentToPrint" source={content} componentProps={{body: {small: true}}} />
      </Body>
      <ContainerActions justifyContent="space-between" marginTop={2} width="100%" gap={2}>
        <Button onClick={handleDownloadTerms} small>
          <FormattedMessage id="platform.selfSignUp.terms.download" />
        </Button>
        <Button small primary onClick={handleAcceptTerms}>
          <FormattedMessage id="platform.selfSignUp.terms.accept" />
        </Button>
      </ContainerActions>
    </StyledModal>
  )
}

export default TermsAndConditions
